import { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, ChevronDown, ChevronUp, Hash } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * LOT B — Alerte pré-facturation : opérations prêtes à facturer dont la
 * référence client manque encore (une facture émise n'est plus modifiable).
 */
export const MissingExternalRefAlert = ({ onOpenOperation }) => {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/external-reference/missing`);
        if (alive) setData(res.data);
      } catch {
        if (alive) setData(null);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (!data || !data.total) return null;

  const blocking = data.blocking > 0;
  const tone = blocking
    ? 'border-red-200 bg-red-50/70 text-red-800'
    : 'border-amber-200 bg-amber-50/70 text-amber-800';

  return (
    <div className={`border rounded-lg px-4 py-3 ${tone}`} data-testid="missing-external-ref-alert">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={2} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">
            {data.total} opération{data.total > 1 ? 's' : ''} à facturer sans référence client
            {blocking && <> — dont <strong>{data.blocking} bloquante{data.blocking > 1 ? 's' : ''}</strong></>}
          </p>
          <p className="text-xs opacity-80 mt-0.5">
            {blocking
              ? "La facturation est refusée tant que la référence n'est pas renseignée."
              : "Renseignez-la avant d'émettre la facture : un document émis n'est plus modifiable."}
          </p>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {data.companies.map((c) => (
              <Badge key={c.companyName} variant="outline" className="text-[11px] bg-white/70">
                {c.companyName} · {c.count} · {c.label}{c.required ? ' (obligatoire)' : ''}
              </Badge>
            ))}
          </div>

          {expanded && (
            <div className="mt-3 max-h-64 overflow-y-auto rounded border border-white/60 bg-white/70 divide-y divide-slate-100">
              {data.operations.map((op) => (
                <button
                  key={op.id}
                  onClick={() => onOpenOperation?.(op)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-50 text-xs"
                  data-testid={`missing-ref-op-${op.id}`}
                >
                  <Hash className="w-3 h-3 text-slate-400" />
                  <span className="font-mono text-slate-800">{op.operationNumber}</span>
                  <span className="text-slate-400 truncate flex-1">{op.departure} → {op.arrival}</span>
                  <span className="text-slate-500">{op.companyName}</span>
                  {op.blocking && <Badge className="bg-red-100 text-red-700 text-[10px]">bloquante</Badge>}
                </button>
              ))}
            </div>
          )}
        </div>
        <Button size="sm" variant="ghost" className="h-7 text-xs shrink-0"
          onClick={() => setExpanded(!expanded)} data-testid="missing-ref-toggle-btn">
          {expanded ? <ChevronUp className="w-3.5 h-3.5 mr-1" /> : <ChevronDown className="w-3.5 h-3.5 mr-1" />}
          {expanded ? 'Masquer' : 'Voir la liste'}
        </Button>
      </div>
    </div>
  );
};

export default MissingExternalRefAlert;
